import React, { useState, useCallback } from "react";

import {
  Autocomplete,
  AutocompleteSearchData,
  Button,
  Select,
  SelectData,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../app/store";
import { useGetCounties } from "../../hooks/codebook/useGetCounties";
import { useGetProgramAges } from "../../hooks/codebook/useGetProgramAges";
import { useGetPrograms } from "../../hooks/codebook/useGetPrograms";
import useInput from "../../hooks/input/useInput";
import { InstitutionFilters } from "../../models/Institution/InstitutionSearch";
import MunicipalityWithCounty from "../../models/Municipality/MunicipalityWithCounty";
import CodeBook from "../../models/Shared/CodeBook";
import {
  getMunicipalityById,
  getRequestMunicipalitiesSearch,
} from "../../slices/codeBookSlice";
import style from "./InstitutionsFilters.module.scss";

interface Props {
  handleSearch: any;
  setFilters: (value: React.SetStateAction<InstitutionFilters>) => void;
  filters: InstitutionFilters;
  handleClear?: () => void;
}

export const InstitutionSearchFilters: React.FC<Props> = ({
  handleSearch,
  setFilters,
  filters,
  handleClear = () => {
    setFilters({} as InstitutionFilters);
  },
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const counties = useGetCounties();
  const programs = useGetPrograms();
  const programAges = useGetProgramAges();

  const [municipalitiesFiltered, setMunicipalitiesFiltered] = useState<
    MunicipalityWithCounty[]
  >(([] as unknown) as MunicipalityWithCounty[]);

  const { handleChange } = useInput();

  const handleSearchMunicipalities = useCallback(
    async (searchData: AutocompleteSearchData) => {
      if (searchData) {
        if (searchData.text) {
          return await dispatch(
            getRequestMunicipalitiesSearch({
              name: searchData.text || "",
              countyId: filters.countyId,
            })
          )
            .then(unwrapResult)
            .then((items) => {
              setMunicipalitiesFiltered(items);
              return {
                valueProp: "id",
                textProp: "name",
                data: items || [],
              } as SelectData<MunicipalityWithCounty>;
            });
        } else if (searchData.value) {
          return await dispatch(
            getMunicipalityById({ municipalityId: Number(searchData.value) })
          )
            .then(unwrapResult)
            .then((m) => {
              var preselected = [m];
              setMunicipalitiesFiltered(preselected);
              return {
                valueProp: "id",
                textProp: "name",
                data: preselected || [],
              } as SelectData<MunicipalityWithCounty>;
            });
        }
      }
      return {
        keyProp: "any",
        valueProp: "id",
        textProp: "name",
        data: [],
      } as SelectData<any>;
    },
    [dispatch, filters.countyId]
  );

  const handleCountyChange = (e: any) => {
    const { value } = e.target;

    setFilters((prev) => ({
      ...prev,
      countyId: value,
      municipalityId: undefined,
    }));
  };

  const handleMunicipalityChange = (e: any) => {
    const { value } = e.target;

    const municipality = municipalitiesFiltered.find((m) => m.id === value);

    if (municipality != null) {
      setFilters((prev) => ({
        ...prev,
        municipalityId: value,
        countyId: prev.countyId ? prev.countyId : municipality.countyId,
      }));
    }
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <Typography variant="smallHeading">
            {t("common.filtersChoice")}
          </Typography>
        </Col>
      </Row>
      <Row className={style.filterRow}>
        <Col md={6}>
          <Select
            label={t("common.county")}
            name={nameof<typeof filters>("countyId")}
            value={filters.countyId}
            onChange={handleCountyChange}
            emptyOption={{ value: undefined, text: "" }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: counties || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={6}>
          <Autocomplete
            label={t("institutionFilter.town")}
            name={nameof<typeof filters>("municipalityId")}
            value={filters.municipalityId}
            onChange={handleMunicipalityChange}
            getOptions={handleSearchMunicipalities}
            minimumInputLength={2}
          />
        </Col>
      </Row>
      <Row className={style.filterRow}>
        <Col md={6}>
          <Select
            label={t("common.programAge")}
            name={nameof<typeof filters>("programAgeId")}
            value={filters.programAgeId}
            onChange={(e) => handleChange(e, setFilters)}
            emptyOption={{ value: undefined, text: "" }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: programAges || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={6}>
          <Autocomplete
            label={t("common.program")}
            name={nameof<typeof filters>("programId")}
            value={filters.programId}
            onChange={(e) => handleChange(e, setFilters)}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: programs || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
      </Row>
      <Row className={style.lastFilterRow}>
        <Col md={6}>
          <TextField
            label={t("institutionFilter.kindergartenInstitution")}
            name={nameof<typeof filters>("name")}
            value={filters.name}
            onChange={(e) => handleChange(e, setFilters)}
          />
        </Col>
        <Col md={6} className={style.searchButtonsColumn}>
          <div className={style.searchButtons}>
            <Button color="primary" onClick={handleSearch}>
              {t("common.search")}
            </Button>
            <Typography color="primary" onClick={handleClear}>
              {t("common.cleanFilters")}
            </Typography>
          </div>
        </Col>
      </Row>
    </>
  );
};
